import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { StorageBlobHttpHeaders, StorageResourceProperties, StorageResourceRef, StorageWriteOutcome } from '../../../../../types/azureStorage';
import type { StorageExplorerMode, StorageExplorerView } from '../../../core/types/workspaceTypes';
import { blobTagsProblem, httpHeaderProblem, metadataProblem } from '../../../../../core/engines/azureStorageWriteRules';
import { cancelStorageRequest, newStorageRequestId, setStorageBlobHeaders, setStorageBlobTags, setStorageMetadata, toStorageOperationError } from '../../services/azureStorageApi';
import { storageBlobPropertiesQueryKey, storageContainerPropertiesQueryKey } from '../../services/storageListingKeys';
import { refreshStorageListingAt } from '../../services/storageRefreshIntent';

type PropertiesRef = Extract<StorageResourceRef, { kind: 'blob' | 'container' }>;
type Section = 'metadata' | 'headers' | 'tags';
/** `conflict` (final UI/UX batch C1, R9 / N5): the server refused the write as changed (412) — the panel shows the
 *  visible conflict notice with Reload and keeps Save off, instead of a muted "Changed on the server · Reload" line. */
export type StorageSaveState = { status: 'idle' | 'saving' | 'saved' | 'error'; message?: string; conflict?: boolean };
export type StorageSaveResult = { ok: false } | { ok: true; reread?: StorageResourceProperties };
export const RELOAD_BEFORE_SAVE_MESSAGE = 'Reload before saving — the version this edit is based on is unknown.';
const EMPTY: Record<Section, StorageSaveState> = { metadata: { status: 'idle' }, headers: { status: 'idle' }, tags: { status: 'idle' } };

function outcomeMessage(outcome: StorageWriteOutcome<unknown>): string {
  return outcome.reread.ok ? 'Saved' : 'Saved — could not read it back; reload to see the current values';
}

function errorState(raw: unknown): StorageSaveState {
  const error = toStorageOperationError(raw);
  if (error.category === 'conflict') return { status: 'error', conflict: true };
  if (error.category === 'ambiguous_outcome') return { status: 'error', message: 'Outcome unknown — reload to check whether it was saved' };
  return { status: 'error', message: error.message };
}

/**
 * Section saves. Each call carries its section's own baseline ETag (and, for tags, baseline map) — never the panel's
 * latest refresh — so a save is conditioned on exactly the version its draft started from (review C F2).
 */
function versionProblem(etag: string | undefined): { message: string } | undefined {
  return etag ? undefined : { message: RELOAD_BEFORE_SAVE_MESSAGE };
}

export function useStoragePropertiesSave(args: {
  connectionId: string;
  resource: PropertiesRef;
  listingContext?: { mode: StorageExplorerMode; parent: StorageResourceRef; view: StorageExplorerView };
}) {
  const { connectionId, resource, listingContext } = args;
  const client = useQueryClient();
  const [states, setStates] = useState<Record<Section, StorageSaveState>>(EMPTY);
  const active = useRef<Partial<Record<Section, { id: string; cancel: () => Promise<void> }>>>({});
  const key = resource.kind === 'blob' ? storageBlobPropertiesQueryKey(connectionId, resource) : storageContainerPropertiesQueryKey(connectionId, resource);

  const save = useCallback(async (
    section: Section,
    problem: { message: string } | undefined,
    invoke: (requestId: string) => Promise<StorageWriteOutcome<StorageResourceProperties>>,
  ): Promise<StorageSaveResult> => {
    if (problem) {
      setStates((current) => ({ ...current, [section]: { status: 'error', message: problem.message } }));
      return { ok: false };
    }
    const requestId = newStorageRequestId();
    let cancelled = false;
    const cancel = async () => {
      if (cancelled) return;
      cancelled = true;
      await cancelStorageRequest(requestId);
    };
    active.current[section] = { id: requestId, cancel };
    setStates((current) => ({ ...current, [section]: { status: 'saving' } }));
    try {
      const result = await invoke(requestId);
      if (active.current[section]?.id !== requestId) return { ok: false };
      if (result.reread.ok) client.setQueryData(key, result.reread.value);
      await client.invalidateQueries({ queryKey: key, exact: true, refetchType: 'none' });
      if (listingContext) {
        refreshStorageListingAt(client, connectionId, listingContext.mode, listingContext.parent, listingContext.view);
      }
      setStates((current) => ({ ...current, [section]: { status: 'saved', message: outcomeMessage(result) } }));
      return { ok: true, reread: result.reread.ok ? result.reread.value : undefined };
    } catch (error) {
      if (active.current[section]?.id !== requestId) return { ok: false };
      setStates((current) => ({ ...current, [section]: errorState(error) }));
      return { ok: false };
    } finally {
      if (active.current[section]?.id === requestId) delete active.current[section];
    }
  }, [client, connectionId, key, listingContext]);

  useEffect(() => () => { for (const request of Object.values(active.current)) void request?.cancel(); }, []);
  return {
    states,
    state: states.metadata,
    /** A section write is in flight — reported apart from the draft, which can be edited back to clean meanwhile (review E1). */
    saving: Object.values(states).some((state) => state.status === 'saving'),
    cancel: async () => { await Promise.all(Object.values(active.current).map((request) => request?.cancel())); },
    /** Forget a section's save outcome (its conflict included) — the section was reloaded or discarded. */
    clear: (section?: Section) => setStates((current) => (section ? { ...current, [section]: { status: 'idle' } } : EMPTY)),
    // Blob: an ETag condition on the wire. Container: none exists (review A MAJOR 3), so main compares the loaded ETag
    // with its pre-write read (Fable F3). Either way a save whose baseline version is unknown is refused.
    saveMetadata: (metadata: Record<string, string>, etag: string | undefined) => save('metadata', metadataProblem(metadata) ?? versionProblem(etag), (requestId) =>
      setStorageMetadata({ connectionId, resource, metadata, ...(resource.kind === 'blob' ? { ifMatch: etag } : { baselineEtag: etag }), requestId, timeoutMs: 30_000 })),
    saveHeaders: (headers: StorageBlobHttpHeaders, etag: string | undefined) => resource.kind === 'blob'
      ? save('headers', httpHeaderProblem(headers) ?? versionProblem(etag), (requestId) => setStorageBlobHeaders({ connectionId, resource, headers, ifMatch: etag, requestId, timeoutMs: 30_000 }))
      : Promise.resolve<StorageSaveResult>({ ok: false }),
    saveTags: (tags: Record<string, string>, baselineTags: Record<string, string>, etag: string | undefined) => resource.kind === 'blob'
      ? save('tags', blobTagsProblem(tags) ?? versionProblem(etag), (requestId) => setStorageBlobTags({ connectionId, resource, tags, baselineTags, ifMatch: etag, requestId, timeoutMs: 30_000 }))
      : Promise.resolve<StorageSaveResult>({ ok: false }),
  };
}
